import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { HeaderPage } from '../../../components/HeaderPage/HeaderPage';
import TextField from 'components/TextField';
import Button from 'components/Button';
import { selectCompany } from 'store/companySlide';
import { CompanyInfo } from 'data/models/company.model';
import {
  getCompanyAiContext,
  updateCompanyAiContext,
} from 'data/api/company.api';
import { CompanyAiContext } from 'data/models/company-ai-context.model';
import { handleTabs, TabEnum } from '../company-detail/CompanDetailNew';

interface AiContextForm {
  careTeam?: string;
  conversationRules?: string;
}

export const CompanyAiContextPage = () => {
  const { company_id } = useParams();
  const companyInfo: CompanyInfo = useSelector(selectCompany);
  const [supportServices, setSupportServices] = React.useState<string>();
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const { handleSubmit, reset, control } = useForm<AiContextForm>({
    mode: 'onChange',
  });

  // React Router keeps this component mounted across /company/:id/ai-context
  // transitions, so a late response for the previous company could reset the
  // form after the new one is current — and Save would then write those
  // values to the new company. Every load is tagged and stale ones dropped.
  const loadId = React.useRef(0);
  // The live route. An async continuation closes over the company_id of the
  // render that started it, so comparing that against itself always passes —
  // every staleness check has to read this ref instead.
  const activeCompanyId = React.useRef(company_id);

  // Takes the company explicitly: a post-save reload must fetch the company
  // it saved, not whichever one the route points at by the time it resolves.
  const fetchAiContext = async (targetCompanyId: string) => {
    const requestId = ++loadId.current;
    try {
      const res: CompanyAiContext = await getCompanyAiContext(targetCompanyId);
      if (
        loadId.current !== requestId ||
        targetCompanyId !== activeCompanyId.current
      )
        return;
      reset({
        careTeam: res?.careTeam,
        conversationRules: res?.conversationRules,
      });
      setSupportServices(res?.supportServices);
      setLoaded(true);
    } catch (error) {
      if (
        loadId.current !== requestId ||
        targetCompanyId !== activeCompanyId.current
      )
        return;
      console.log(error);
      toast.error('Failed to load AI context');
    }
  };

  React.useEffect(() => {
    // Ref first: an in-flight save resolving after this point must see the
    // new company and skip its reload.
    activeCompanyId.current = company_id;
    // Blocks Save until this company's own values have arrived, and clears
    // the previous company's values so they can never sit under the new
    // company's header (including when the new load fails).
    setLoaded(false);
    reset({ careTeam: '', conversationRules: '' });
    setSupportServices('');
    if (company_id) {
      void fetchAiContext(company_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company_id]);

  const onSubmit = async (values: AiContextForm) => {
    if (!loaded || saving || !company_id) return;
    // Pinned for the whole round trip; the route may change mid-flight.
    const savedCompanyId = company_id;
    // Locked before the await: edits made during the save would otherwise be
    // wiped by the success refresh, and a second submit could land out of
    // order and leave the older payload stored.
    setSaving(true);
    try {
      const res = await updateCompanyAiContext(savedCompanyId, values);
      if (res?.success) {
        toast.success('Update success!');
        if (savedCompanyId === activeCompanyId.current) {
          // Closes editing for the refresh too — it resets the form.
          setLoaded(false);
          void fetchAiContext(savedCompanyId);
        }
      } else {
        toast.error('Update failed!');
      }
    } catch (error) {
      toast.error('Update failed!');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <HeaderPage
        title={{ title: companyInfo?.name || '', hasBack: true }}
        tabs={handleTabs(
          company_id || '',
          companyInfo?.isParentCompany || false,
          TabEnum.AiContext,
        )}
      />

      <div className="nv-container">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={3} sx={{ maxWidth: 720, mt: 3 }}>
            <Typography variant="body2" sx={{ color: '#4A5568' }}>
              Facts entered here are given to the AI when it drafts care-partner
              replies for this organization&apos;s members. They stay scoped to
              this organization.
            </Typography>

            {/* Editing is closed until this company's values land: the load
                resets the form, which would silently discard anything typed
                while it was still in flight. */}
            <TextField
              name="careTeam"
              label="Care Team & Roles"
              control={control}
              placeholder="e.g. Maria Lopez — lead care partner; Dr. Nguyen — clinical director"
              multiline
              minRows={3}
              disabled={!loaded || saving}
            />

            <TextField
              name="conversationRules"
              label="Conversation Guidance"
              control={control}
              placeholder="Org-specific do's and don'ts, tone, boundaries"
              multiline
              minRows={4}
              disabled={!loaded || saving}
            />

            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: '#213B54', mb: 1 }}
              >
                Support Services
              </Typography>
              <Box
                sx={{
                  border: '1px solid #EFEEF0',
                  borderRadius: '8px',
                  p: 2,
                  color: supportServices ? '#213B54' : '#79747E',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {supportServices || 'None set'}
              </Box>
              <Typography variant="caption" sx={{ color: '#79747E' }}>
                Edited on the Escalation tab; included in AI drafts.
              </Typography>
            </Box>

            <Box>
              <Button
                type="submit"
                variant="contained"
                disabled={!loaded || saving}
              >
                Save
              </Button>
            </Box>
          </Stack>
        </form>
      </div>
    </>
  );
};

export default CompanyAiContextPage;
